/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-use-before-define */
/* eslint-disable sonarjs/cognitive-complexity */
/**
 * special roam handler for axes
 * see common handler in './roamHelper.ts'
 */

import * as GraphicType from 'echarts/types/src/util/graphic';
import { type ElementAnimateConfig } from 'zrender/lib/Element.js';
import { ControllerHost } from './roamHelper';

// TODO:目前只对笛卡尔坐标系做了适配
export interface AxesControllerHost extends ControllerHost {
  axes: 'xAxis' | 'yAxis';
}

export function doUpdateAxesViewOnPanAndZoom(
  controllerHost: AxesControllerHost,
  {
    dx = 0,
    dy = 0,
    zoomDelta = 1,
    zoomX = 0,
    zoomY = 0,
    dirty = true
  }: {
    dx?: number;
    dy?: number;
    zoomDelta?: number;
    zoomX?: number;
    zoomY?: number;
    dirty?: boolean;
  },
  animation?: boolean | ElementAnimateConfig
) {
  const { target, zoomLimit, axes } = controllerHost;
  // 记录原始zoom用于还原
  const originControllerHostZoom = controllerHost.zoom;
  // 计算zoomScale
  controllerHost.zoom = controllerHost.zoom || 1;
  let newZoom = controllerHost.zoom;
  newZoom *= zoomDelta;
  if (zoomLimit) {
    const zoomMin = zoomLimit.min || 0;
    const zoomMax = zoomLimit.max || Infinity;
    newZoom = Math.max(Math.min(zoomMax, newZoom), zoomMin);
  }

  const zoomScale = newZoom / controllerHost.zoom;
  // Keep the mouse center when scaling
  controllerHost.zoom = newZoom;

  // 指定各类元素的zoom的transform function
  const zoomFnMap = {
    axisLine: getAxisLineZoomTransform,
    splitLine: getLineZoomTransform,
    tick: getLineZoomTransform,
    label: getLabelZoomTransform
  };

  // 分类收集元素
  const elementArr: {
    type: 'axisLine' | 'splitLine' | 'tick' | 'label';
    el: GraphicType.Line | GraphicType.Text;
  }[] = [];

  // 收集各类内容
  target.traverse(d => {
    if (!d.anid) {
      return;
    }

    // 轴线
    if (d.anid === 'line') {
      elementArr.push({
        type: 'axisLine',
        el: d as unknown as GraphicType.Line
      });
      return;
    }

    // 分割线以及轴线
    if (d.anid.includes('line')) {
      elementArr.push({
        type: 'splitLine',
        el: d as unknown as GraphicType.Line
      });
      return;
    }

    // tick
    if (d.anid.includes('tick')) {
      elementArr.push({
        type: 'tick',
        el: d as unknown as GraphicType.Line
      });
      return;
    }

    // label
    if (d.anid.includes('label')) {
      elementArr.push({
        type: 'label',
        el: d as unknown as GraphicType.Text
      });
    }
  });

  // 记录初始值
  const originTransform: {
    el: GraphicType.Line | GraphicType.Text;
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
  }[] = [];

  // 计算各类元素的pan和zoom的transform变量
  elementArr.forEach(d => {
    const { type, el } = d;

    // 记录原始值
    originTransform.push({
      el,
      x: el.x,
      y: el.y,
      scaleX: el.scaleX,
      scaleY: el.scaleY
    });

    // 计算要应用的transform
    const panTransform = getElementPanTransform(axes, dx, dy);
    const zoomTransform = zoomFnMap[type](el as any, zoomScale, zoomX, zoomY, axes);
    const transform = {
      x: panTransform.x + zoomTransform.x,
      y: panTransform.y + zoomTransform.y,
      scaleX: zoomTransform.scaleX,
      scaleY: zoomTransform.scaleY
    };

    if (!animation) {
      el.x += transform.x;
      el.y += transform.y;
      el.scaleX *= transform.scaleX;
      el.scaleY *= transform.scaleY;
    } else {
      el.animateTo(transform, animation === true ? {} : animation);
    }
  });

  if (!animation && dirty) {
    target.dirty();
  }

  return () => {
    controllerHost.zoom = originControllerHostZoom;
    originTransform.forEach(d => {
      const { el } = d;
      el.x = d.x;
      el.y = d.y;
      el.scaleX = d.scaleX;
      el.scaleY = d.scaleY;
    });
  };
}

function getElementPanTransform(axes: 'xAxis' | 'yAxis', dx: number, dy: number) {
  return {
    x: axes === 'xAxis' ? dx : 0,
    y: axes === 'yAxis' ? dy : 0
  };
}

function getAxisLineZoomTransform(
  axisLine: GraphicType.Line,
  zoomScale: number,
  zoomX: number,
  zoomY: number,
  axes: 'xAxis' | 'yAxis'
) {
  return {
    x: axes === 'xAxis' ? (axisLine.x - zoomX) * (zoomScale - 1) : 0,
    y: axes === 'yAxis' ? (axisLine.y - zoomY) * (zoomScale - 1) : 0,
    scaleX: axes === 'xAxis' ? zoomScale : 1,
    scaleY: axes === 'yAxis' ? zoomScale : 1
  };
}

function getLineZoomTransform(
  splitLine: GraphicType.Line,
  zoomScale: number,
  zoomX: number,
  zoomY: number,
  axes: 'xAxis' | 'yAxis'
) {
  return {
    x: axes === 'xAxis' ? (splitLine.x + splitLine.shape.x1 - zoomX) * (zoomScale - 1) : 0,
    y: axes === 'yAxis' ? (splitLine.y + splitLine.shape.y1 - zoomY) * (zoomScale - 1) : 0,
    scaleX: 1,
    scaleY: 1
  };
}

function getLabelZoomTransform(
  label: GraphicType.Text,
  zoomScale: number,
  zoomX: number,
  zoomY: number,
  axes: 'xAxis' | 'yAxis'
) {
  return {
    x: axes === 'xAxis' ? (label.x - zoomX) * (zoomScale - 1) : 0,
    y: axes === 'yAxis' ? (label.y - zoomY) * (zoomScale - 1) : 0,
    scaleX: 1,
    scaleY: 1
  };
}
